import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DatabaseBackup, Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

// Bloc « Sauvegardes » de Réglages : affiché au Patron seulement (le serveur refuse aussi les autres rôles).
// Aucun téléchargement depuis le navigateur : la restauration se fait avec backend/restore_backup.py.

const PARIS = { timeZone: "Europe/Paris" };
const fmtParis = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const day = d.toLocaleDateString("fr-FR", { ...PARIS, day: "2-digit", month: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("fr-FR", { ...PARIS, hour: "2-digit", minute: "2-digit" });
  return `${day} à ${time}`;
};
const fmtSize = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} o`;
  const [v, u] = n < 1024 ** 2 ? [n / 1024, "Ko"] : [n / 1024 ** 2, "Mo"];
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} ${u}`;
};
const STORAGE_LABELS = {
  s3: "Stockage en ligne privé (S3)",
  local: "Dossier local du serveur (backups_local)",
};

const Row = ({ label, children, testid }) => (
  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-4 py-2 border-t border-border first:border-t-0" data-testid={testid}>
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm font-medium sm:text-right break-words">{children}</span>
  </div>
);

export default function BackupSection() {
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/backups/status")
    .then((r) => { setStatus(r.data); setLoadError(""); })
    .catch((e) => setLoadError(apiError(e.response?.data?.detail))), []);

  useEffect(() => { load(); }, [load]);

  // Sauvegarde lancée ailleurs (la nuit, autre onglet) : on suit son avancement.
  useEffect(() => {
    if (!status?.running || busy) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [status?.running, busy, load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backups/run");
      setStatus(data);
      toast.success("Sauvegarde terminée");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    } finally { setBusy(false); }
  };

  const running = busy || !!status?.running;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card p-4 sm:p-6 space-y-4" data-testid="backup-section">
      <div>
        <h3 className="font-display font-bold text-xl flex items-center gap-2"><DatabaseBackup className="h-5 w-5 text-accent shrink-0" /> Sauvegardes</h3>
        <p className="text-xs text-muted-foreground mt-1">Copie complète de la base, chaque nuit à 3 h (heure de Paris), conservée 30 jours (jamais moins des 7 dernières). Visible par le Patron seulement.</p>
      </div>

      {loadError && <p className="text-sm text-destructive" data-testid="backup-load-error">{loadError}</p>}

      {status && (
        <div>
          <Row label="Dernière sauvegarde" testid="backup-last">
            {status.last ? `${fmtParis(status.last.date)} (${fmtSize(status.last.size)})` : "Aucune pour l'instant"}
          </Row>
          <Row label="Sauvegardes conservées" testid="backup-count">{status.count}</Row>
          <Row label="Lieu de stockage" testid="backup-storage">{STORAGE_LABELS[status.storage] || status.storage}</Row>
          <Row label="Prochaine sauvegarde prévue" testid="backup-next">{fmtParis(status.next_run)}</Row>
        </div>
      )}

      {status?.last_error && (
        <div className="flex gap-2 rounded-xl border border-destructive/50 bg-destructive/10 p-3 text-destructive" data-testid="backup-error">
          <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-sm min-w-0">
            <div className="font-semibold">Échec de la dernière sauvegarde, le {fmtParis(status.last_error_at)}</div>
            <div className="text-xs mt-1 break-words">{status.last_error}</div>
          </div>
        </div>
      )}
      {status?.list_error && (
        <div className="flex gap-2 rounded-xl border border-destructive/50 bg-destructive/10 p-3 text-destructive" data-testid="backup-list-error">
          <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="text-sm min-w-0">
            <div className="font-semibold">Impossible de lire la liste des sauvegardes</div>
            <div className="text-xs mt-1 break-words">{status.list_error}</div>
          </div>
        </div>
      )}

      <Button type="button" onClick={runNow} disabled={running || !status} className="rounded-full gap-1.5 w-full sm:w-auto" data-testid="backup-run">
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseBackup className="h-4 w-4" />}
        {running ? "Sauvegarde en cours…" : "Sauvegarder maintenant"}
      </Button>
    </div>
  );
}
